import { T, useTranslate } from '@tolgee/react';
import { Dropdown, Menu, Slider, Tooltip } from 'antd';
import { uniqBy } from 'es-toolkit';
import Link from 'next/link';
import { useRouter } from 'next/router';
import {
  Dispatch,
  SetStateAction,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import Hotkeys from 'react-hot-keys';
import { useIntercom } from 'react-use-intercom';
import styled, { css } from 'styled-components';

import { ArrowLeftSvg, ExitSvg, PlusSvg, SettingsSvg } from '../../../assets/icons';
import ArrowRightSvg from '../../../assets/icons/ArrowRightSvg';
import ChartSvg from '../../../assets/icons/ChartSvg';
import DragDropSvg from '../../../assets/icons/DragDropSvg';
import ExternalLinkSvg from '../../../assets/icons/ExternalLinkSvg';
import FreezeSvg from '../../../assets/icons/FreezeSvg';
import FullScreenSvg from '../../../assets/icons/FullScreenSvg';
import LobbyParticipantsSvg from '../../../assets/icons/LobbyParticipantsSvg';
import MinimizeSvg from '../../../assets/icons/MinimizeSvg';
import MuteSvg from '../../../assets/icons/MuteSvg';
import ShieldSvg from '../../../assets/icons/ShieldSvg';
import SkipSvg from '../../../assets/icons/SkipSvg';
import SpeakerBarlessSvg from '../../../assets/icons/SpeakerBarless';
import SpeakerHalfFullSvg from '../../../assets/icons/SpeakerHalfFull';
import SpeakerSvg from '../../../assets/icons/SpeakerSvg';
import theme from '../../../assets/theme';
import useAccessBadge, { UserAccessBadge } from '../../../hooks/useAccessBadge';
import useAttioEventTracking from '../../../hooks/useAttioEventTracking';
import useAudio, { DEFAULT_AUDIO_VOLUME } from '../../../hooks/useAudio';
import useAutoFade from '../../../hooks/useAutoFade';
import { useBreakPoint } from '../../../hooks/useBreakPoint';
import {
  TemplateStateActions,
  useDesignerDataContext,
} from '../../../hooks/useDesignerLogic';
import useEndGame from '../../../hooks/useEndGame';
import useFullScreen from '../../../hooks/useFullScreen';
import useGameSaveSocket from '../../../hooks/useGameSaveSocket';
import useLobby from '../../../hooks/useLobby';
import { useMetrics } from '../../../hooks/useMetrics';
import { useOnOutsideClick } from '../../../hooks/useOnOutsideClick';
import usePreviewGame from '../../../hooks/usePreviewGame';
import useTemplate from '../../../hooks/useTemplate';
import useTrackActivation from '../../../hooks/useTrackActivation';
import { LiveActionsDisabledContext } from '../../../providers/LiveActionsDisabledProvider/LiveActionsDisabledProvider';
import { TimesyncContext } from '../../../providers/TimesyncProvider/TimesyncProvider';
import { Game, PartialParticipant } from '../../../services/backendService/types';
import Metrics from '../../../services/metrics';
import { isTouchDevice } from '../../../utils/client';
import { debounceEventIfNotTrusted, notUndefined } from '../../../utils/general';
import {
  calculateVolume,
  findRoundSong,
  GameMusicContext,
  MusicState,
} from '../../../utils/musicUtil';
import { isSafari } from '../../../utils/navigator';
import { RoundState } from '../../../utils/roundUtils';
import { zIndexes } from '../../../utils/zIndexes';
import { Button } from '../../_atoms/Button';
import { DropdownButtonItem } from '../../_atoms/DropdownButton/DropdownButton';
import { IconWrapper } from '../../_atoms/IconWrapper';
import { LoadingDots } from '../../_atoms/StreamingLongAnswerList/StreamingLongAnswerList';
import AddNewActivity from '../../_molecules/AddNewActivity';
import BaseModal from '../../_molecules/BaseModal';
import LobbyDropdown from '../../_molecules/LobbyDropdown';
import { Slide, SlideType } from '../../SlideComponents/slide.types';
import {
  findRelatedSlidesWithoutIndex,
  getAggregatedSlidesAndActivities,
} from '../../SlideComponents/slideUtil';
import { getRoundState, toActivity } from '../LiveGame';

const SimpleLiveGameActionButtons = ({
  slides,
  game,
  currentSlide,
  isNavigationDisabled,
  tenantId,
  roundState,
  setShowMedia,
  participants: rawParticipants,
  participantsCount,
  showActivityMedia,
  isEndGameModalVisible,
  setGameModalVisible,
}: {
  slides: Slide[];
  currentSlide: Slide;
  isNavigationDisabled: boolean;
  game: Game;
  tenantId: string;
  participants: PartialParticipant[];
  participantsCount: number;
  roundState?: RoundState;
  setShowMedia: Dispatch<SetStateAction<boolean>>;
  showActivityMedia: boolean;
  setRoundState?: Dispatch<SetStateAction<RoundState | undefined>>;
  isEndGameModalVisible: boolean;
  setGameModalVisible: Dispatch<SetStateAction<boolean>>;
}) => {
  const { t } = useTranslate();
  const router = useRouter();
  const { autoSave } = useGameSaveSocket();
  const { isFullScreen, closeFullScreen, goFullScreen } = useFullScreen();
  const { setShowLobby, showLobby } = useLobby(game?.id);

  const [showSettings, setShowSettings] = useState(false);
  const isLargerThan500 = useBreakPoint(500);
  const isLargerThan650 = useBreakPoint(650);
  const metrics = useMetrics();
  const { trackEvent } = useIntercom();
  const { template, autoSaveTemplate, isTemplateOwner } = useTemplate(game.templateId);
  const localTemplateDispatch = useDesignerDataContext(context => context.dispatch);
  const { isPreview } = usePreviewGame();
  const [isHovering, setIsHovering] = useState(false);
  const { now } = useContext(TimesyncContext);
  const { accessBadge: userAccessBadge } = useAccessBadge();
  const trackAttioEvent = useAttioEventTracking();
  const { trackActivation } = useTrackActivation();

  // Same filtering as LobbyDropdown - in preview mode with requireRealNames,
  // only count participants who have entered their name (blue bubbles)
  const participants = useMemo(() => {
    if (game.isPreviewGame && game.requireRealNames) {
      return rawParticipants.filter(p => p.name !== undefined && p.name !== '');
    }
    return rawParticipants;
  }, [rawParticipants, game.isPreviewGame, game.requireRealNames]);

  const numberOfSlides = useMemo(() => {
    return getAggregatedSlidesAndActivities(slides || []).length;
  }, [slides]);

  const currentRelatedSlide = useMemo(() => {
    return currentSlide
      ? findRelatedSlidesWithoutIndex(currentSlide.id, slides)[0]
      : undefined;
  }, [currentSlide, slides]);
  const currentRelatedSlideIndex = useMemo(() => {
    const activityAndContentSlides = getAggregatedSlidesAndActivities(slides || []);
    return currentRelatedSlide
      ? activityAndContentSlides.indexOf(currentRelatedSlide)
      : 0;
  }, [currentRelatedSlide, slides]);

  const handleOpenCurimote = () => {
    metrics.logGameEvent(
      'LiveGame.Curimote.Open',
      {
        gameId: game.id,
      },
      isPreview,
    );
    closeFullScreen(() => {
      window.open(
        `${window.location.origin}/${tenantId}/games/${game.id}/curimote`,
        'curimote',
        `width=${window.innerWidth * 0.6},height=${window.innerHeight * 0.6}`,
      );
    });
  };

  const handleKPress = () => {
    handleOpenCurimote();
  };

  const handleSave = (gameToSave: Game) => {
    autoSave({
      game: gameToSave,
    });
    // saves the current round on the template in order to navigate to corret round in creator

    setShowMedia(false);
  };
  const handleSkipBack = () => {
    // If overlay is showing, close it first
    if (game.isComplete) {
      // if the game is complete, we need to uncomplete it
      handleSave({ ...game, isComplete: false });
      return;
    }

    if (!isNavigationDisabled && currentRelatedSlideIndex > 0 && currentSlide) {
      const currentRelatedSlides = findRelatedSlidesWithoutIndex(currentSlide.id, slides);
      const firstRelatedSlide = currentRelatedSlides[0];
      const firstRelatedSlideIndex = slides.findIndex(
        slide => slide.id === firstRelatedSlide.id,
      );
      const previousSlide = slides[firstRelatedSlideIndex - 1];
      const related = findRelatedSlidesWithoutIndex(previousSlide.id, slides);
      const wasPlayed = related.some(s => s.wasPlayed);
      const newSlide = related[wasPlayed ? related.length - 1 : 0];
      const gameToSave = {
        ...game,
        currentRound: newSlide.id,
        roundStartsAt: null,
        modifiedAt: now(),
      };
      handleSave(gameToSave);
    }
  };

  const handleSkipForward = () => {
    // If we're on the last slide and not showing the overlay, show it
    if (
      !isNavigationDisabled &&
      currentRelatedSlideIndex >= numberOfSlides - 1 &&
      !game.isComplete
    ) {
      handleSave({ ...game, isComplete: true });
      return;
    }

    // Normal forward navigation
    if (
      !isNavigationDisabled &&
      currentRelatedSlideIndex + 1 < numberOfSlides &&
      currentSlide
    ) {
      const currentRelatedSlides = findRelatedSlidesWithoutIndex(currentSlide.id, slides);
      const lastRelatedSlide = currentRelatedSlides[currentRelatedSlides.length - 1];
      const lastRelatedRoundIndex = slides.findIndex(
        slide => slide.id === lastRelatedSlide.id,
      );
      const nextSlide = slides[lastRelatedRoundIndex + 1];
      const related = findRelatedSlidesWithoutIndex(nextSlide.id, slides);
      const wasPlayed = related.some(r => r.wasPlayed);
      const updatedSlides = slides.map(r =>
        r.id === currentSlide.id && (r.type === 'podium' || r.type === 'responsegrid')
          ? { ...r, wasPlayed: true }
          : r,
      );

      const gameToSave = {
        ...game,
        currentRound: related[wasPlayed ? related.length - 1 : 0].id,
        roundStartsAt: null,
        slides: updatedSlides,
        modifiedAt: now(),
      };
      handleSave(gameToSave);
    }
  };

  const handleFreezePress = () => {
    metrics.logEvent('LiveGame.Settings.Freeze.Clicked', {
      participantsCount,
    });
    if (game.roundFreezedAt) {
      if (!game.roundStartsAt) {
        // you have unfreezed the slide before you startedt it
        const gameToSave = {
          ...game,
          roundFreezedAt: null,
        };
        autoSave({
          game: gameToSave,
        });
      } else {
        // you have unfreezed the slide during an ongoing slide
        const durationFreezed = now() - game.roundFreezedAt;
        const updatedRoundStartsAt = game.roundStartsAt + durationFreezed;
        const gameToSave = {
          ...game,
          roundStartsAt: updatedRoundStartsAt,
          roundFreezedAt: null,
        };
        autoSave({
          game: gameToSave,
        });
      }
    } else {
      // you have freezed the slide
      const gameToSave = {
        ...game,
        roundFreezedAt: now(),
      };
      autoSave({
        game: gameToSave,
      });
    }
  };

  const relatedSlides = useMemo(
    () =>
      currentSlide && game.slides
        ? findRelatedSlidesWithoutIndex(currentSlide.id, game.slides)
        : undefined,
    [currentSlide, game.slides],
  );

  const handleSkipVoting = useCallback(() => {
    if (game.slides && relatedSlides) {
      metrics.logEvent('LiveGame.SkipVoting.Clicked');
      const slideIndex = game.slides.findIndex(r => r.id === currentSlide.id);
      const relatedSlideIds = relatedSlides.map(r => r.id);
      const filteredSlides = (game.slides || []).filter(
        r => !relatedSlideIds.includes(r.id),
      );

      const newSlides: Slide[] = [
        relatedSlides[0],
        {
          ...relatedSlides[relatedSlides.length - 1],
          dataReferenceSlideId: relatedSlides[0].id,
          type: 'responsegrid',
        },
      ];

      filteredSlides.splice(slideIndex, 0, ...newSlides);
      autoSave({
        game: {
          ...game,
          slides: filteredSlides,
          modifiedAt: now(),
          currentRound: newSlides[1].id,
          roundFreezedAt: null,
        },
      });
    }
  }, [game, relatedSlides, metrics, autoSave, now, currentSlide.id]);
  const { isInvisible } = useAutoFade({
    delay: 1000 * 10,
    eventListenerDependency: router.pathname,
    callback: () => {
      setShowSettings(false);
    },
  });
  const { end, endResultIsLoading } = useEndGame();
  const handleEndPresentation = useCallback(() => {
    metrics.logEvent(
      'LiveGame.EndPresentation',
      buildEndLessonMetadata({
        game,
        tenantId,
        userAccessBadge,
        currentSlide,
        roundState,
      }),
    );
    end({
      gameId: game.id,
    });
    autoSaveTemplate({
      templateConfig: {
        activeGame: null,
        modifiedAt: new Date().getTime(),
        id: game.templateId,
      },
    });
    localTemplateDispatch({ type: TemplateStateActions.endGame });
    router.push(`/${tenantId}/reports/${game.id}`);
    if (isFullScreen) {
      closeFullScreen();
    }
  }, [
    metrics,
    game,
    tenantId,
    userAccessBadge,
    currentSlide,
    end,
    autoSaveTemplate,
    localTemplateDispatch,
    router,
    isFullScreen,
    closeFullScreen,
    roundState,
  ]);

  const { showMediaIsDisabled } = useContext(LiveActionsDisabledContext);

  const canShowMediaButton = useMemo(() => {
    const canShow = !game.isShowingLobby && !showMediaIsDisabled;
    const type = currentSlide?.type;
    if (type === 'responsegrid') return canShow;
    if (type === 'podium') return canShow;
    if (type === 'wordcloud' && roundState === 'post') return canShow;
    if (type === 'poll' && roundState === 'post') return canShow;
    return false;
  }, [currentSlide?.type, game.isShowingLobby, roundState, showMediaIsDisabled]);

  const handleStartPressed = useCallback(() => {
    // This method can only be invoked when the join screen is visible.

    if (participants.length === 0) {
      metrics.logGameEvent('LiveGame.StartButton.NoParticipants');
      return;
    }

    if (!game.slides || game.slides.length === 0) {
      metrics.logGameEvent('LiveGame.StartButton.NoSlides');
      /*
        TODO how can we prevent this from happening.
       */
      return;
    }

    if (game.isPlaying === true) {
      // The join screen is being shown and we just want to close it.

      if (showLobby) {
        //Close the join screen if it is open
        setShowLobby(false);
      } else {
        // This should never happen.
        metrics.logGameEvent('LiveGame.StartButton.CloseJoinScreenButNotOpen');
      }
      return;
    }

    if (game.isPlaying === false) {
      /*
         We have not yet started the lesson.
         We need to track the start.
         We need to close the join screen.
         We need to enforce the next side to be the first slide of the lesson.
      */
      const generatorUsed = game.slides
        .map(s => s.generatorId)
        .filter(notUndefined)
        .filter((value, index, self) => self.indexOf(value) === index); // filters out duplicates

      // Logging active game for the first time a game is played
      const metadata = {
        isFromDiscover: game.isFromDiscover,
        participants: participantsCount,
        rounds: 0,
        slidesCount: game.slides.length,
        gameId: game.id,
        templateId: game.templateId,
        generatorUsed: generatorUsed,
        grade: template?.metadata?.grade,
        subject: template?.metadata?.subject,
        isDynamic: template?.metadata?.isDynamic,
        language: template?.metadata?.language,
        isShowLobby: showLobby,
        selectedCardId: template?.metadata?.selectedCardId,
        skeleton: template?.metadata?.skeleton,
        uploadedSlidesFileId: template?.metadata?.uploadedSlidesFileId,
        blocks: template?.content.slides?.map(s => s.blockLabel).filter(notUndefined),
        blockIds: template?.content.slides?.map(s => s.blockId).filter(notUndefined),
      };

      metrics.logGameEvent('Live.Start', metadata, game.isPreviewGame);
      if (participantsCount > 2) {
        Metrics.incrementGamePlayed();
        trackEvent('StartGame', metadata);
        trackActivation({
          userAgent: navigator.userAgent,
          eventType: 'RealLiveLessonStarted',
        });
        // Paused until decision on product event
        // if (userAccessBadge?.user?.profileInfo?.role !== 'Teacher') {
        //   trackAttioEvent('non_teacher_lesson_started', {
        //     organizationId: userAccessBadge?.organization?.id,
        //     userRole: userAccessBadge?.user?.profileInfo?.role,
        //     userId: userAccessBadge?.user?.id,
        //     email: userAccessBadge?.user?.email,
        //     firstName: userAccessBadge?.user?.firstName,
        //     lastName: userAccessBadge?.user?.lastName,
        //     participants: participantsCount,
        //   });
        // }
      } else {
        Metrics.incrementTestGamePlayed();
        trackEvent('StartTestGame', metadata);
      }

      // Logging the unique generators that are used for this lesson
      const allGeneratorIds = game.slides.map(s => s.generatorId).filter(notUndefined);
      const uniqueGeneratorIds = Array.from(new Set(allGeneratorIds));

      uniqueGeneratorIds.forEach(generatorId => {
        metrics.logGameEvent('Live.Start.Generator.Played', {
          generatorId,
          participants: participantsCount,
        });
      });

      autoSave({
        game: {
          ...game,
          isPlaying: true, // make sure we mark the lesson as started
          lastPlayedAt: now(), // make sure we mark the lesson as started
          modifiedAt: now(),
          roundFreezedAt: null,
          isShowingLobby: false, // close the join screen
          currentRound: game.slides[0].id, // Force set the current round to the first slide
        },
        onSuccessFunction: () => {
          if (
            !game.isFromDiscover &&
            template &&
            !template.content.activeGame?.isOngoing &&
            game.templateId
          ) {
            // This is needed until the backend sets the active game on the template when the game is started.
            autoSaveTemplate({
              templateConfig: {
                // make sure isPreview doesnt alter activeGame
                activeGame: game.isPreviewGame
                  ? undefined
                  : {
                      id: game.id,
                      isOngoing: true,
                    },
                id: game.templateId,
                modifiedAt: new Date().getTime(),
                gameIds: !game.isPreviewGame ? [game.id] : [],
              },
            });
          }
        },
      });
    }
  }, [
    autoSave,
    autoSaveTemplate,
    game,
    metrics,
    now,
    participants.length,
    participantsCount,
    setShowLobby,
    showLobby,
    template,
    trackActivation,
    trackEvent,
  ]);

  return (
    <>
      <Hotkeys keyName="k" onKeyUp={handleKPress} />

      <Hotkeys
        keyName="l"
        onKeyUp={() => {
          if (!showLobby) {
            metrics.logEvent('LiveGame.Lobby.Open', { byLClick: true });
          } else {
            metrics.logEvent('LiveGame.Lobby.Close', { byLClick: true });
            if (participants.length < 1) {
              return; // do not close lobby if there are no participants
            }
          }
          setShowLobby(!showLobby);
        }}
      />
      <BaseModal
        width="400px"
        centered
        visible={isEndGameModalVisible && !game.isComplete}
        title={
          endResultIsLoading
            ? t(
                'Loading the report for this lesson',
                'Loading the report for this lesson',
              )
            : t('Do you want to end this lesson?', 'Do you want to end this lesson?')
        }
      >
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'flex-end',
            gap: 10,
          }}
        >
          <Button
            type="ghost"
            styles={{
              border: 'none',
              boxShadow: 'none',
            }}
            onClick={() => {
              setGameModalVisible(false);
            }}
          >
            <T keyName="Cancel" defaultValue="Cancel" />
          </Button>

          <Button
            type={'danger'}
            styles={{
              border: 'none',
              color: theme.colors.white,
              boxShadow: 'none',
            }}
            loading={endResultIsLoading || game.isComplete}
            onClick={e => {
              debounceEventIfNotTrusted({
                cb: handleEndPresentation,
                isTrusted: e.isTrusted,
              });
            }}
          >
            {endResultIsLoading || game.isComplete ? (
              <T keyName="Loading report" defaultValue="Loading report" />
            ) : (
              <T keyName="End" defaultValue="End" />
            )}
          </Button>
        </div>
      </BaseModal>
      {!game.isComplete && (
        <>
          <LobbyDropdown
            setShowLobby={setShowLobby}
            game={game}
            showLobby={showLobby}
            tenantId={tenantId}
            participants={participants}
            participantsCount={participantsCount}
          />
          <LiveGameActionButtonContainer
            onMouseEnter={() => setIsHovering(true)}
            onMouseLeave={() => setIsHovering(false)}
            $isInvisible={!game.isShowingLobby && isInvisible && !isHovering}
          >
            {canShowMediaButton && isLargerThan650 && (
              <ShowMediaAction
                showMedia={showActivityMedia}
                setShowMedia={setShowMedia}
              />
            )}

            <OpenModerationAction
              tenantId={tenantId}
              game={game}
              isContentFilterEnabled={game.isContentFilterEnabled}
            />
            <div style={{ display: 'flex', height: 48, gap: 5 }}>
              {isLargerThan500 && <VolumeAction game={game} showLobby={showLobby} />}
              {showLobby && (
                <ShowFullScreenAction
                  isFullScreen={isFullScreen}
                  toggleFullScreen={() => {
                    if (!isPreview) {
                      metrics.logEvent('LiveGame.FullScreen.Clicked');
                      if (isFullScreen) {
                        closeFullScreen();
                      } else {
                        goFullScreen();
                      }
                    }
                  }}
                />
              )}
              <SettingsAction
                userAccessBadge={userAccessBadge}
                game={game}
                tenantId={tenantId}
                currentSlide={currentRelatedSlide}
                showSettings={showSettings}
                setShowSettings={setShowSettings}
                isTemplateOwner={!!isTemplateOwner}
                showSkipVoting={
                  (currentSlide.type === 'answers' || currentSlide.type === 'canvas') &&
                  !currentSlide.preventSkipVotingRound &&
                  roundState === 'post-between' &&
                  relatedSlides &&
                  relatedSlides.length > 2
                }
                handleSkipVoting={handleSkipVoting}
                setShowLobby={setShowLobby}
                showMediaAction={canShowMediaButton && !isLargerThan650}
                setShowMedia={setShowMedia}
                showMedia={showActivityMedia}
                setEndGameModalVisible={setGameModalVisible}
                handleFreezePress={handleFreezePress}
              />
            </div>
            <LiveGameWidgetDivider />
            {!game.isShowingLobby && !game.isComplete && (
              <LiveGameNavigationActions
                isNavigationDisabled={isNavigationDisabled}
                currentSlideType={currentRelatedSlide?.type}
                numberOfSlides={numberOfSlides}
                currentSlideRoundIndex={currentRelatedSlideIndex}
                roundState={roundState}
                showEndOfLessonOverlay={game.isComplete}
                handleSkipBack={() => {
                  metrics.logEvent('LiveGame.GoBack.Clicked');
                  if (showLobby) {
                    setShowLobby(false);
                  } else {
                    handleSkipBack();
                  }
                }}
                handleSkipForward={() => {
                  metrics.logEvent('LiveGame.GoForward.Clicked');
                  if (showLobby) {
                    metrics.logEvent('LiveGame.Lobby.Close', { byForwardClick: true });
                    setShowLobby(false);
                  } else {
                    handleSkipForward();
                  }
                }}
              />
            )}
            {game.isShowingLobby && (
              <Tooltip
                title={
                  participants.length < 1
                    ? t(
                        'You need at least one student to start the lesson',
                        'You need at least one student to start the lesson',
                      )
                    : ''
                }
              >
                <OrangeButton
                  $disabled={participants.length < 1}
                  onClick={e => {
                    metrics.logEvent('LiveGame.StartButtonLobby.Click', {
                      isTrusted: e.isTrusted,
                      clickType: e.type,
                      isPlaying: game.isPlaying,
                      isShowingLobby: showLobby,
                      participantsCount: participants.length,
                    });
                    debounceEventIfNotTrusted({
                      cb: handleStartPressed,
                      isTrusted: e.isTrusted,
                    });
                  }}
                >
                  <span>
                    {participants.length < 1 ? (
                      <span>
                        <T keyName="Waiting" defaultValue="Waiting" />
                        <LoadingDots>...</LoadingDots>
                      </span>
                    ) : game.isPlaying && showLobby ? (
                      <T keyName="Close join screen" defaultValue="Close join screen" />
                    ) : (
                      <T keyName="Ready" defaultValue="Ready" />
                    )}
                  </span>
                </OrangeButton>
              </Tooltip>
            )}
          </LiveGameActionButtonContainer>
        </>
      )}
    </>
  );
};

const OrangeButton = styled(Button)<{ $disabled?: boolean }>`
  background-color: ${theme.colors.orange};
  width: 180px;
  height: 42px;
  border-radius: 5px;

  span {
    font-size: 20px;
  }

  &:hover,
  &:focus,
  &:active {
    background-color: ${theme.colors.orangeDark};
  }

  ${({ $disabled }) =>
    $disabled &&
    `
    background-color: ${theme.colors.black};
    color: ${theme.colors.greyLight};
    
    &:hover,
    &:focus,
    &:active {
      background-color: ${theme.colors.black};
      color: ${theme.colors.greyLight};
    }

  `}
`;

const SettingsAction = ({
  userAccessBadge,
  game,
  tenantId,
  isTemplateOwner,
  currentSlide,
  setShowSettings,
  showSettings,
  showSkipVoting,
  handleSkipVoting,
  setShowLobby,
  showMediaAction,
  setShowMedia,
  showMedia,
  setEndGameModalVisible,
  handleFreezePress,
}: {
  userAccessBadge: UserAccessBadge | undefined;
  game: Game;
  showSettings: boolean;
  setShowSettings: Dispatch<SetStateAction<boolean>>;
  isTemplateOwner: boolean;
  tenantId: string;
  currentSlide?: Slide;
  showSkipVoting: boolean | undefined;
  handleSkipVoting: () => void;
  setShowLobby: (show?: boolean | undefined) => void;
  showMediaAction: boolean;
  setShowMedia: Dispatch<SetStateAction<boolean>>;
  showMedia: boolean;
  setEndGameModalVisible: Dispatch<SetStateAction<boolean>>;
  handleFreezePress: () => void;
}) => {
  const metrics = useMetrics();
  const isDesktop = useBreakPoint(1000);
  const { now } = useContext(TimesyncContext);
  const roundState = getRoundState({ game, now });

  const containerRef = useRef(null);
  useOnOutsideClick(
    containerRef,
    () => {
      setShowSettings(false);
    },
    {
      classNameMatch: ['ant-dropdown-menu-item'],
    },
  );

  const { closeFullScreen } = useFullScreen();
  const router = useRouter();

  const { isPreview } = usePreviewGame();
  const { t } = useTranslate();
  const [showAddActivityModal, setShowAddActivityModal] = useState(false);

  const handleRouteBack = useCallback(() => {
    if (isTemplateOwner) {
      router.push(`/${tenantId}/lessons/${game?.templateId}/edit`);
    } else {
      //if you are not the owner you opened from the workspace we navigate back
      router.back();
    }
  }, [game.templateId, router, tenantId, isTemplateOwner]);

  const handleExit = useCallback(() => {
    closeFullScreen();
    setShowSettings(false);
    if (game.isPlaying) {
      metrics.logGameEvent(
        'LiveGame.Exit.EndLesson',
        buildEndLessonMetadata({
          game,
          tenantId,
          userAccessBadge,
          currentSlide,
          roundState,
        }),
      );
      setEndGameModalVisible(true);
    } else {
      metrics.logGameEvent('LiveGame.Exit.BeforeStart', {
        isFromDiscover: game.isFromDiscover,
      });
      handleRouteBack();
    }
  }, [
    closeFullScreen,
    currentSlide,
    game,
    handleRouteBack,
    metrics,
    setEndGameModalVisible,
    setShowSettings,
    tenantId,
    userAccessBadge,
    roundState,
  ]);

  const settingsMenu = useCallback(
    () => (
      <Menu
        style={{
          backgroundColor: 'transparent',
          padding: 0,
          paddingBottom: '10px',
          boxShadow: 'none',
          width: isDesktop ? 400 : 300,
        }}
      >
        <MenuContainer ref={containerRef} style={{ paddingTop: 10, paddingBottom: 10 }}>
          <>
            {!isPreview && (
              <MenuItem key="exit" onClick={handleExit}>
                <DropdownButtonItem
                  isDarkTheme={true}
                  icon={<ExitSvg />}
                  title={
                    game.isPlaying
                      ? t('End the lesson', 'End the lesson')
                      : isTemplateOwner
                      ? t('Back to designer', 'Back to designer')
                      : t('Back to workspace', 'Back to workspace')
                  }
                  description={
                    game.isPlaying
                      ? t(
                          'End the lesson and see the report',
                          'End the lesson and see the report',
                        )
                      : isTemplateOwner
                      ? t('Go back to the designer', 'Go back to the designer')
                      : t('Go back to the workspace', 'Go back to the workspace')
                  }
                />
              </MenuItem>
            )}
            <FullScreenAction />
            <MenuItem
              onClick={() => {
                setShowAddActivityModal(true);
                setShowSettings(false);
                metrics.logEvent('LiveGame.Settings.AddActivity.Show');
              }}
            >
              <DropdownButtonItem
                isDarkTheme={true}
                icon={<PlusSvg />}
                title={t('Add activity')}
                description={t('Add a new activity on the fly')}
              />
            </MenuItem>
            {showMediaAction && (
              <MenuItem
                onClick={() => {
                  setShowSettings(false);
                  setShowMedia(e => !e);
                }}
              >
                <DropdownButtonItem
                  isDarkTheme={true}
                  icon={showMedia ? <ChartSvg /> : <DragDropSvg />}
                  title={
                    showMedia
                      ? t('Show results', 'Show results')
                      : t('Show Slide', 'Show Slide')
                  }
                  description={
                    showMedia
                      ? t(
                          'Show the results of the activity',
                          'Show the results of the activity',
                        )
                      : t('Show the slide', 'Show the slide')
                  }
                />
              </MenuItem>
            )}
            <MenuItem
              onClick={() => {
                metrics.logEvent('LiveGame.Settings.Lobby.Open', {
                  byParticipantsAction: true,
                });
                setShowLobby(true);
              }}
              disabled={!!game.isShowingLobby}
            >
              <DropdownButtonItem
                isDarkTheme={true}
                icon={<LobbyParticipantsSvg />}
                title={t('See students', 'See students')}
                description={
                  !!game.isShowingLobby
                    ? t(
                        'The join screen is currently open',
                        'The join screen is currently open',
                      )
                    : t(
                        'Open the join screen to see students',
                        'Open the join screen to see students',
                      )
                }
              ></DropdownButtonItem>
            </MenuItem>
            <MenuItem>
              <DropdownButtonItem
                isDarkTheme={true}
                onSwitchToggled={handleFreezePress}
                includeSwitch
                switchValue={!!game.roundFreezedAt}
                icon={<FreezeSvg />}
                title={'Freeze'}
                description={t('Freeze all student screens')}
              />
            </MenuItem>
            {showSkipVoting && (
              <MenuItem
                onClick={() => {
                  setShowSettings(false);
                  handleSkipVoting();
                }}
              >
                <DropdownButtonItem
                  isDarkTheme={true}
                  icon={<SkipSvg />}
                  title={t('Skip voting', 'Skip voting')}
                  description={t('Skip the voting round', 'Skip the voting round')}
                />
              </MenuItem>
            )}
          </>
        </MenuContainer>
      </Menu>
    ),
    [
      isDesktop,
      isPreview,
      handleExit,
      game.isPlaying,
      game.isShowingLobby,
      game.roundFreezedAt,
      t,
      isTemplateOwner,
      showMediaAction,
      showMedia,
      handleFreezePress,
      showSkipVoting,
      setShowSettings,

      metrics,
      setShowMedia,
      setShowLobby,
      handleSkipVoting,
    ],
  );
  return (
    <>
      <BaseModal
        visible={showAddActivityModal}
        onCancel={() => setShowAddActivityModal(false)}
      >
        <AddNewActivity
          gameId={game.id}
          roundState={roundState}
          currentSlide={currentSlide}
          setShow={setShowAddActivityModal}
        />
      </BaseModal>
      <Dropdown visible={showSettings} placement="topRight" overlay={settingsMenu}>
        <Tooltip title={t('Settings', 'Settings')}>
          <LiveGameActionButton
            onClick={() => {
              setShowSettings(e => !e);
            }}
            className="ant-dropdown-menu-item"
            $isActive={showSettings}
          >
            <IconWrapper width="25px" height="25px">
              <SettingsSvg />
            </IconWrapper>
          </LiveGameActionButton>
        </Tooltip>
      </Dropdown>
    </>
  );
};

const ShowMediaAction = ({
  showMedia,
  setShowMedia,
}: {
  showMedia: boolean;
  setShowMedia: Dispatch<SetStateAction<boolean>>;
}) => {
  const { t } = useTranslate();
  const metrics = useMetrics();
  return (
    <>
      <Tooltip
        title={
          showMedia
            ? t('Show the results of the activity', 'Show the results of the activity')
            : t('Show the full slide', 'Show the full slide')
        }
      >
        <LiveGameActionButton
          style={{
            borderColor: theme.colors.white,
            color: theme.colors.white,
            display: 'flex',
            justifyContent: 'space-evenly',
            alignItems: 'center',
          }}
          $isActive={showMedia}
          onClick={() => {
            metrics.logEvent('LiveGame.Settings.ShowMedia.Clicked');
            setShowMedia(e => !e);
          }}
        >
          <IconWrapper width="25px" height="25px" style={{ marginRight: '10px' }}>
            {showMedia ? <ChartSvg /> : <DragDropSvg />}
          </IconWrapper>
          <span>
            {showMedia
              ? t('Show results', 'Show results')
              : t('Show Slide', 'Show Slide')}
          </span>
        </LiveGameActionButton>
      </Tooltip>
      <LiveGameWidgetDivider />
    </>
  );
};

const FullScreenAction = () => {
  const metrics = useMetrics();
  const { isFullScreen, closeFullScreen, goFullScreen } = useFullScreen();
  const toggleFullScreen = () => {
    if (!isPreview) {
      metrics.logEvent('LiveGame.FullScreen.Clicked');
      if (isFullScreen) {
        closeFullScreen();
      } else {
        goFullScreen();
      }
    }
  };
  const { t } = useTranslate();
  const { isPreview } = usePreviewGame();

  return (
    <Tooltip
      title={
        isPreview
          ? t('Disabled in preview')
          : isFullScreen
          ? t('Close full screen')
          : t('Open full screen')
      }
      placement={'top'}
      mouseEnterDelay={2}
    >
      <MenuItem onClick={() => toggleFullScreen()}>
        <DropdownButtonItem
          isDarkTheme={true}
          icon={isFullScreen ? <MinimizeSvg /> : <FullScreenSvg />}
          title={
            isFullScreen
              ? t('Close fullscreen', 'Close fullscreen')
              : t('Open fullscreen', 'Open fullscreen')
          }
        />
      </MenuItem>
    </Tooltip>
  );
};

const ContentFilterIndicatorContainer = styled.div<{ $enabled: boolean }>`
  position: absolute;
  top: -32px;
  left: 50%;
  transform: translateX(-50%);
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: ${props =>
    props.$enabled ? 'rgba(40,47,51,0.85)' : theme.colors.red};
  border-radius: 5px 5px 0px 0px;
  padding: 5px 20px 5px 20px;
  color: white;
  font-size: 12px;
  font-weight: bold;
  height: 22px;
  span {
    line-height: 14px;
    font-size: 12px;
    text-wrap: nowrap;
  }
`;

function buildEndLessonMetadata({
  game,
  tenantId,
  userAccessBadge,
  currentSlide,
  roundState,
}: {
  game: Game;
  tenantId: string;
  userAccessBadge: UserAccessBadge | undefined;
  currentSlide: Slide | undefined;
  roundState?: RoundState;
}) {
  return {
    gameId: game.id,
    isFromDiscover: game.isFromDiscover,
    linkToPresentation: `${window.location.origin}/${tenantId}/lessons/${game.templateId}`,
    linkToReport: `${window.location.origin}/${tenantId}/reports/${game.id}`,
    presentationId: game.templateId,
    tenantId,
    userId: userAccessBadge?.user?.id,
    slides: game.slides?.map(
      ({ id, type, wasPlayed, blockId, generatorId, durationSeconds }) => ({
        blockLabel: blockId?.split('_')[1],
        durationSeconds,
        generatorId,
        id,
        type,
        wasPlayed,
      }),
    ),
    currentSlideType: currentSlide?.type,
    isPlayingActivity: roundState === 'in',
    // this should be a list of blocks contained by the lesson
    // must count blocks used multiple times, but not count each slide within a block multiple times
    blocks: uniqBy(
      game.slides?.filter(b => !!b.blockId) ?? [],
      ({ blockId }) => blockId,
    ).map(({ blockLabel }) => blockLabel),
    currentSlideOffsetFromLast: findNumberOfSlidesRemaining({
      currentSlide,
      game,
    }),
  };
}

const OpenModerationAction = ({
  isContentFilterEnabled,
  game,
  tenantId,
}: {
  isContentFilterEnabled?: boolean;
  game: Game;
  tenantId: string;
}) => {
  const isDesktop = useBreakPoint(1000);
  const { t } = useTranslate();

  const [isOptionsOpen, setIsOptionsOpen] = useState(false);

  const containerRef = useRef(null);
  useOnOutsideClick(
    containerRef,
    () => {
      setIsOptionsOpen(false);
    },
    {
      classNameMatch: ['moderation-menu'],
    },
  );

  const metrics = useMetrics();

  const { autoSave } = useGameSaveSocket();

  const handleContentFilteringToggle = useCallback(
    (isEnabled: boolean) => {
      autoSave({
        game: {
          ...game,
          isContentFilterEnabled: isEnabled,
        },
      });
    },
    [autoSave, game],
  );

  const curimoteLink = useMemo(() => {
    return window
      ? `${window.location.origin}/${tenantId}/games/${game.id}/curimote`
      : undefined;
  }, [game.id, tenantId]);

  const { closeFullScreen } = useFullScreen();

  const moderationSettingsMenu = useCallback(
    () => (
      <Menu
        style={{
          backgroundColor: 'transparent',
          padding: 0,
          paddingBottom: '10px',
          boxShadow: 'none',
          width: isDesktop ? 400 : 300,
        }}
      >
        <MenuContainer
          ref={containerRef}
          style={{ paddingTop: 10, paddingBottom: 10 }}
          className="moderation-menu"
        >
          <>
            <MenuItem>
              <DropdownButtonItem
                noHoverEffect={true}
                isDarkTheme={true}
                showOptions
                includeSwitch
                switchValue={game.isContentFilterEnabled}
                onSwitchToggled={handleContentFilteringToggle}
                icon={<ShieldSvg />}
                description={
                  <>
                    {game.isContentFilterEnabled
                      ? t(
                          'Student responses detected as inappropriate by our AI moderation system will be automatically dismissed',
                          'Student responses detected as inappropriate by our AI moderation system will be automatically dismissed',
                        )
                      : t(
                          'Student responses will be shown unless manually dismissed',
                          'Student responses will be shown unless manually dismissed',
                        )}
                  </>
                }
                title={
                  game.isContentFilterEnabled
                    ? t('Content filtering: On', 'Content filtering: On')
                    : t('Content filtering: Off', 'Content filtering: Off')
                }
              />
            </MenuItem>
            <MenuItem>
              <DropdownButtonItem
                noHoverEffect={true}
                isDarkTheme={true}
                showOptions
                icon={<ExternalLinkSvg />}
                description={
                  <>
                    <T
                      keyName="Guide, monitor and manage student behavior during class activities in a seperate window."
                      defaultValue="Guide, monitor and manage student behavior during class activities in a seperate window."
                    />{' '}
                    <Link
                      href="https://curipod.com/c/how-to-moderate-inappropriate-students-in-curipod"
                      passHref
                      aria-label={t(
                        'link to moderation information',
                        'link to moderation information',
                      )}
                      prefetch={false}
                    >
                      <a
                        style={{
                          color: theme.colors.white,
                          textDecoration: 'underline',
                        }}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        <T keyName="Read more" defaultValue="Read more" />
                      </a>
                    </Link>
                  </>
                }
                title={'Open moderation tool'}
                qrLink={curimoteLink}
                onCustomClick={() => {
                  setIsOptionsOpen(false);
                  closeFullScreen(() => {
                    metrics.logEvent('LiveGame.Settings.Curimote.Open');
                    window.open(
                      `${window.location.origin}/${tenantId}/games/${game.id}/curimote`,
                      'curimote',
                      `width=${window.innerWidth * 0.6},height=${window.innerHeight *
                        0.6}`,
                    );
                  });
                }}
                pin={game.pin || undefined}
              />
            </MenuItem>
          </>
        </MenuContainer>
      </Menu>
    ),
    [
      isDesktop,
      game.isContentFilterEnabled,
      game.pin,
      game.id,
      handleContentFilteringToggle,
      t,
      curimoteLink,
      closeFullScreen,
      metrics,
      tenantId,
    ],
  );

  const handleToggleModerationMenu = useCallback((isOptionsOpen: boolean) => {
    setIsOptionsOpen(isOptionsOpen);
  }, []);

  const [isHoveringTooltip, setIsHoveringTooltip] = useState(false);

  return (
    <Dropdown
      visible={isOptionsOpen}
      placement="topRight"
      overlay={moderationSettingsMenu}
      className="moderation-menu"
    >
      <>
        <Tooltip
          visible={isHoveringTooltip && !isOptionsOpen}
          //@ts-expect-error
          onMouseEnter={() => setIsHoveringTooltip(true)}
          onMouseLeave={() => setIsHoveringTooltip(false)}
          placement={game.isShowingLobby ? 'left' : 'top'}
          title={t(
            'Guide, monitor and manage student behavior during class activities in a separate window.',
            'Guide, monitor and manage student behavior during class activities in a separate window.',
          )}
        >
          <LiveGameActionButton
            className="moderation-menu"
            onClick={e => {
              e.preventDefault();
              e.stopPropagation();
              handleToggleModerationMenu(!isOptionsOpen);
            }}
          >
            {game.isShowingLobby && (
              <ContentFilterIndicatorContainer
                $enabled={!!isContentFilterEnabled}
                className="moderation-menu"
              >
                <span>
                  {isContentFilterEnabled && (
                    <T
                      keyName="Moderation.AutoFiltering.On"
                      defaultValue="Auto filtering: on"
                    />
                  )}
                  {!isContentFilterEnabled && (
                    <T
                      keyName="Moderation.AutoFiltering.Off"
                      defaultValue="Auto filtering: off"
                    />
                  )}
                </span>
              </ContentFilterIndicatorContainer>
            )}
            <IconWrapper width="20px" height="25px" style={{ marginRight: '10px' }}>
              <ShieldSvg />
            </IconWrapper>
            <span>
              <T keyName="Content Moderation" defaultValue="Content Moderation" />
            </span>
          </LiveGameActionButton>
        </Tooltip>
        <LiveGameWidgetDivider />
      </>
    </Dropdown>
  );
};
const ShowFullScreenAction = ({
  toggleFullScreen,
  isFullScreen,
}: {
  toggleFullScreen: () => void;
  isFullScreen: boolean;
}) => {
  const { t } = useTranslate();
  return (
    <>
      <Tooltip
        title={
          isFullScreen
            ? t('Exit full screen', 'Exit full screen')
            : t('Go full screen', 'Go full screen')
        }
      >
        <LiveGameActionButton
          onClick={e => {
            debounceEventIfNotTrusted({
              cb: toggleFullScreen,
              isTrusted: e.isTrusted,
            });
          }}
        >
          <IconWrapper width="20px" height="25px" style={{ marginRight: '0px' }}>
            {isFullScreen ? <MinimizeSvg /> : <FullScreenSvg />}
          </IconWrapper>
        </LiveGameActionButton>
      </Tooltip>
    </>
  );
};

const VolumeAction = ({ game, showLobby }: { game: Game; showLobby: boolean }) => {
  const { now } = useContext(TimesyncContext);
  const [music, setMusic] = useState<MusicState>({
    autoplay: true,
    isLooping: true,
    song: undefined,
  });
  const [percievedVolume, setPercievedVolume] = useState(
    window.location.hostname === 'localhost' ? 0 : 0.6,
  );
  const isLargerthan800 = useBreakPoint(800);

  const [lastVolume, setLastVolume] = useState(DEFAULT_AUDIO_VOLUME);
  const metrics = useMetrics();
  const { isPreview } = usePreviewGame();
  const { volume, setVolume, playOrPauseAudio } = useAudio(
    music.song,
    music.autoplay,
    music.isLooping,
  );
  const useClickDropdown = isTouchDevice() && !isLargerthan800;

  // Set low volume by default in preview mode
  useEffect(() => {
    if (isPreview) {
      setVolume(0);
      setPercievedVolume(0);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleMPress = () => {
    handleToggle();
  };

  const handleToggle = () => {
    if (isSafari) {
      playOrPauseAudio(volume !== 0 ? false : true);
    }
    if (volume !== 0) {
      setLastVolume(percievedVolume);
      setVolume(0);
      setPercievedVolume(0);
    } else {
      setVolume(calculateVolume(lastVolume));
      setPercievedVolume(lastVolume);
      setLastVolume(0);
    }
    // setMusic(m => ({ ...m, isPlaying: newState }));
    metrics.logGameEvent('LiveGame.Music.Toggle', { isPlaying: volume > 0 }, isPreview);
  };

  const handleVolumeChange = (vol: number) => {
    setVolume(calculateVolume(vol));
    setPercievedVolume(vol);
  };
  useEffect(() => {
    if (game) {
      const nextMusic = findRoundSong(game, now, showLobby);
      if (nextMusic === null && music.song !== undefined) {
        // stop playing
        setMusic(m => ({
          ...m,
          isLooping: false,
          song: undefined,
        }));
        return;
      }
      if (nextMusic && nextMusic.song !== music.song) {
        setMusic(m => ({
          ...m,
          isLooping: nextMusic.loop,
          song: nextMusic.song,
        }));
      }
    }
  }, [game, music, now, showLobby]);
  return (
    <GameMusicContext.Provider value={{ music, setMusic }}>
      <Hotkeys keyName="m" onKeyUp={handleMPress} />
      <DropDownButtonContainer id="volumecontainer">
        <Dropdown
          placement="top"
          trigger={useClickDropdown ? ['click'] : ['hover']}
          overlay={() =>
            !isSafari ? (
              <div style={{ backgroundColor: 'transparent', padding: '20px' }}>
                <SliderContainer>
                  <Slider
                    reverse={false}
                    vertical
                    id="volume-slider"
                    tipFormatter={null}
                    value={percievedVolume}
                    onChange={vol => {
                      handleVolumeChange(vol);
                    }}
                    min={0}
                    max={1}
                    step={0.01}
                  />
                </SliderContainer>
              </div>
            ) : (
              <></>
            )
          }
        >
          <LiveGameActionButton onClick={handleToggle}>
            <IconWrapper color={theme.colors.white} width={'25px'} height={'25px'}>
              {volume !== 0 ? (
                volume > 0 ? (
                  volume > 0.8 ? (
                    <SpeakerSvg />
                  ) : (
                    <SpeakerHalfFullSvg />
                  )
                ) : (
                  <SpeakerBarlessSvg />
                )
              ) : (
                <MuteSvg />
              )}
            </IconWrapper>
          </LiveGameActionButton>
        </Dropdown>
      </DropDownButtonContainer>
    </GameMusicContext.Provider>
  );
};

const LiveGameNavigationActions = ({
  currentSlideType,
  numberOfSlides,
  currentSlideRoundIndex,
  isNavigationDisabled,
  handleSkipBack,
  handleSkipForward,
  roundState,
  showEndOfLessonOverlay,
}: {
  currentSlideType?: SlideType;
  numberOfSlides: number;
  currentSlideRoundIndex: number;
  isNavigationDisabled: boolean;
  handleSkipBack?: () => void;
  handleSkipForward?: () => void;
  roundState?: RoundState;
  showEndOfLessonOverlay?: boolean;
}) => {
  const { t } = useTranslate();
  const isLargerThan900 = useBreakPoint(900);

  const rightArrowColor = useMemo(() => {
    // Don't show orange arrow when overlay is visible
    if (showEndOfLessonOverlay) {
      return 'transparent';
    }
    // Show orange arrow for slides that can navigate forward, but not on the last slide
    if (
      (currentSlideType === 'slide' || roundState === 'post') &&
      currentSlideRoundIndex < numberOfSlides - 1 &&
      !isNavigationDisabled
    ) {
      return theme.colors.orange;
    }
    return 'transparent';
  }, [
    currentSlideType,
    roundState,
    currentSlideRoundIndex,
    numberOfSlides,
    isNavigationDisabled,
    showEndOfLessonOverlay,
  ]);

  const { arrowKeyIsDisabled } = useContext(LiveActionsDisabledContext);
  return (
    <NavigationButtons>
      {arrowKeyIsDisabled !== true && (
        <>
          <Hotkeys keyName="left" onKeyUp={handleSkipBack} />
          <Hotkeys keyName="up" onKeyUp={handleSkipBack} />
          <Hotkeys keyName="down" onKeyUp={handleSkipForward} />
          <Hotkeys keyName="right" onKeyUp={handleSkipForward} />
        </>
      )}
      <Tooltip
        title={
          showEndOfLessonOverlay
            ? t('Back to slides', 'Back to slides')
            : currentSlideRoundIndex < 1
            ? t('This is the first slide', 'This is the first slide')
            : t('Previous slide', 'Previous slide')
        }
      >
        <LiveGameActionButton
          $disabled={
            !showEndOfLessonOverlay &&
            (currentSlideRoundIndex < 1 || isNavigationDisabled)
          }
          style={{ marginRight: '2px' }}
          onClick={handleSkipBack}
        >
          <IconWrapper
            color="white"
            width="40px"
            height="40px"
            style={{
              cursor:
                showEndOfLessonOverlay ||
                (!showEndOfLessonOverlay && currentSlideRoundIndex >= 1)
                  ? 'pointer'
                  : 'not-allowed',
            }}
          >
            <ArrowLeftSvg />
          </IconWrapper>
        </LiveGameActionButton>
      </Tooltip>

      <NavigateSlideTitle className={'slide-title'}>
        <span style={{ marginBottom: '-5px' }}>
          {isLargerThan900 && toActivity(currentSlideType || '')}
        </span>

        <span
          style={{
            whiteSpace: 'nowrap',
            width: '100%',
            fontSize: '16px',
            marginTop: '-5px',
          }}
        >
          {currentSlideRoundIndex + 1}/{numberOfSlides}
        </span>
      </NavigateSlideTitle>
      <Tooltip
        title={
          showEndOfLessonOverlay
            ? t('This is the end of lesson', 'This is the end of lesson')
            : currentSlideRoundIndex >= numberOfSlides - 1
            ? ''
            : t('Next slide', 'Next slide')
        }
      >
        <LiveGameActionButton
          $buttonColor={rightArrowColor}
          style={{
            marginRight: '12px',
          }}
          $disabled={
            showEndOfLessonOverlay ||
            currentSlideRoundIndex >= numberOfSlides ||
            isNavigationDisabled
          }
          onClick={() => {
            if (handleSkipForward) handleSkipForward();
          }}
        >
          <IconWrapper
            color="white"
            width="40px"
            height="40px"
            style={{
              cursor:
                showEndOfLessonOverlay || currentSlideRoundIndex >= numberOfSlides
                  ? 'not-allowed'
                  : 'pointer',
            }}
          >
            <ArrowRightSvg />
          </IconWrapper>
        </LiveGameActionButton>
      </Tooltip>
    </NavigationButtons>
  );
};

const MenuContainer = styled.div`
  display: grid;
  grid-auto-flow: row;
  background-color: ${theme.colors.translucentLightBlack};
  border-radius: 5px;
  box-shadow: 0px 4px 4px rgb(0, 0, 0, 0.8);
`;
const MenuItem = styled(Menu.Item)`
  background-color: transparent !important;
  color: white;
  opacity: 0.9;

  &:hover {
    opacity: 1;
  }
`;
const LiveGameActionButton = styled.button<{
  $isActive?: boolean;
  $disabled?: boolean;
  $buttonColor?: string;
}>`
  position: relative;
  border: none;
  transition-duration: 0.5s;
  min-height: 30px;
  height: 100%;
  padding: 5px;
  display: flex;
  justify-content: center;
  align-items: center;
  height: 100%;
  min-width: 60px;
  border-radius: 5px;
  font-size: 1.2em;
  color: white;
  background-color: ${({ $isActive }) => ($isActive ? '#656570' : 'transparent')};
  filter: ${({ $disabled }) => ($disabled ? 'brightness(50%)' : 'brightness(100%)')};
  cursor: ${({ $disabled }) => ($disabled ? 'not-allowed' : 'pointer')};
  svg {
    cursor: ${({ $disabled }) => ($disabled ? 'not-allowed' : 'pointer')};
  }
  outline: none;
  &:hover {
    background-color: ${({ $isActive }) => ($isActive ? 'transparent' : '#656570')};
  }

  ${({ $buttonColor }) =>
    $buttonColor &&
    css`
      background-color: ${$buttonColor};

      &:hover,
      &:focus,
      &:active {
        background-color: ${$buttonColor}99;
      }
    `}
`;
const LiveGameActionButtonContainer = styled.div<{ $isInvisible: boolean }>`
  opacity: ${({ $isInvisible }) => ($isInvisible ? '0' : '1')};
  transition: 0.3s;
  position: fixed;
  bottom: 10px;
  transform: translateX(0%);
  right: 10px;
  justify-content: space-evenly;
  font-size: 12px;
  display: grid;
  align-items: center;
  gap: 12px;
  grid-auto-flow: column;
  justify-content: space-evenly;

  background-color: ${theme.colors.translucentLightBlack};
  padding: 10px;
  border-radius: 5px;
  box-shadow: 0px 2px 10px 0px rgba(0, 0, 0, 0.25);
  z-index: ${zIndexes('PRESENTER_ACTION_BUTTONS')};
  @media (max-width: 900px) {
    gap: 2px;
  }
`;
const DropDownButtonContainer = styled.div`
  transition: height, 0.1s, transform 0.2s;
  border-radius: 5px;
  height: 100%;
  position: relative;
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  overflow: hidden;
  background-color: 'transparent';//${theme.colors.translucentLightBlack};
`;
const SliderContainer = styled.div`
  z-index: ${zIndexes('PRESENTER_ACTION_BUTTON_MUSIC_SLIDER')};
  background-color: ${theme.colors.greyDark};
  height: 150px;
  border-radius: 5px;
  padding: 10px;
  padding-top: 20px;
  padding-bottom: 20px;
`;
const NavigationButtons = styled.div`
  display: flex;
  align-items: center;
  height: 50px;
  color: white;
`;

const LiveGameWidgetDivider = styled.div`
  margin: 0;
  border-right: solid 1px ${theme.colors.grey};
  height: 100%;
`;

const NavigateSlideTitle = styled.div`
  width: 170px;
  display: flex;
  text-align: center;
  height: 100%;
  flex-wrap: wrap;
  justify-content: center;
  font-size: 20px;
  align-items: center;
  cursor: default;
  padding: 0 5px;
  svg {
    cursor: default;
  }
  font-weight: bold;
  @media (max-width: 900px) {
    width: fit-content;
    margin: 0px 5px;

    span {
      margin-bottom: 0px !important;
      margin-top: 0px !important;
    }
  }
`;

export default SimpleLiveGameActionButtons;

function findNumberOfSlidesRemaining({
  currentSlide,
  game,
}: {
  currentSlide: Slide | undefined;
  game: Game;
}) {
  const numSlides = game?.slides?.length;
  const indexOfCurrentSlide = (game.slides ?? []).findIndex(
    s => s.id === currentSlide?.id,
  );
  if (numSlides == null || indexOfCurrentSlide < 0 || currentSlide?.id == null) {
    return null;
  }
  const numSlidesRemaining = numSlides - (indexOfCurrentSlide + 1);
  return numSlidesRemaining;
}
